import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import { ModalFooter } from 'src/modules/shared/components/molecules/modals/modal-footer';
import { Text } from 'src/modules/shared/components/atoms/text';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import Loading from 'src/modules/shared/components/atoms/loading';
import { useTriggers } from 'src/modules/shared/hooks/api/automations/triggers';
import { useAutomationsWorkflows } from 'src/modules/shared/hooks/api/automations/workflows/workflows';
import { useWorkflowVersion } from 'src/modules/shared/hooks/api/automations/workflows/version';
import { useCreateWorkflowTriggerMutation } from 'src/modules/shared/hooks/api/automations/workflows/workflows';
import type { TCTrigger, WorkflowMetadata } from 'src/api/generated';
import { type MappingValue } from './mapping-row.tsx';
import { InputMappingSection } from '../shared/input-mapping-section';
import { useAlert } from 'src/contexts/alert-context';

interface CreateTriggerModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialWorkflowId?: string;
  initialTriggerId?: string;
  onCreated?: () => void;
}

function getRequiredFieldsFromSchema(schema: unknown): Set<string> {
  if (!schema || typeof schema !== 'object') return new Set();
  const s = schema as { required?: string[] };
  return new Set(Array.isArray(s.required) ? s.required : []);
}

function getPropertyKeysFromSchema(schema: unknown): string[] {
  if (!schema || typeof schema !== 'object') return [];
  const s = schema as { properties?: Record<string, unknown> };
  return s.properties ? Object.keys(s.properties) : [];
}

export const CreateWorkflowTriggerModal: React.FC<CreateTriggerModalProps> = ({
  isOpen,
  onClose,
  initialWorkflowId,
  initialTriggerId,
  onCreated,
}) => {
  const { showAlert } = useAlert();
  const [selectedTriggerId, setSelectedTriggerId] = useState<string>(
    initialTriggerId || ''
  );
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>(
    initialWorkflowId || ''
  );
  const [selectedVersion] = useState<string>('DRAFT');

  const { triggers, loading: triggersLoading } = useTriggers(50, isOpen);
  const {
    workflows,
    loading: workflowsLoading,
    error: workflowsError,
  } = useAutomationsWorkflows(50, isOpen);

  const { workflowVersion, loading: versionLoading } = useWorkflowVersion(
    selectedWorkflowId,
    selectedVersion,
    isOpen && !!selectedWorkflowId
  );

  const createMutation = useCreateWorkflowTriggerMutation();

  useEffect(() => {
    if (initialTriggerId) setSelectedTriggerId(initialTriggerId);
  }, [initialTriggerId]);

  useEffect(() => {
    if (initialWorkflowId) setSelectedWorkflowId(initialWorkflowId);
  }, [initialWorkflowId]);

  const triggerOptions = useMemo(
    () =>
      [{ value: '', content: '-- Seleccionar --' }].concat(
        triggers.map((t: TCTrigger) => ({
          value: String(t.triggerId),
          content: t.name ? `${t.name} (${t.triggerId})` : String(t.triggerId),
        }))
      ),
    [triggers]
  );

  const workflowOptions = useMemo(
    () =>
      [{ value: '', content: '-- Seleccionar --' }].concat(
        workflows.map((w: WorkflowMetadata) => ({
          value: w.workflowId,
          content: w.name || w.workflowId,
        }))
      ),
    [workflows]
  );

  const inputSchema = (
    workflowVersion?.definition as unknown as {
      inputSchema?: unknown;
    }
  )?.inputSchema;

  const fieldKeys = useMemo(
    () => getPropertyKeysFromSchema(inputSchema),
    [inputSchema]
  );
  const requiredFields = useMemo(
    () => getRequiredFieldsFromSchema(inputSchema),
    [inputSchema]
  );

  const isRecordFn = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null;

  const inferLiteralTypeFromSchema = useCallback(
    (node: unknown): 'string' | 'number' | 'boolean' | 'json' | 'null' => {
      if (!isRecordFn(node)) return 'json';
      const type = (node as Record<string, unknown>).type;
      if (typeof type === 'string') {
        if (type === 'string') return 'string';
        if (type === 'number' || type === 'integer') return 'number';
        if (type === 'boolean') return 'boolean';
        if (type === 'null') return 'null';
        return 'json';
      }
      if (Array.isArray(type)) {
        const preferred = type.find((t) => t !== 'null');
        if (preferred === 'string') return 'string';
        if (preferred === 'number' || preferred === 'integer') return 'number';
        if (preferred === 'boolean') return 'boolean';
        if (preferred === 'null') return 'null';
        return 'json';
      }
      const anyOf = (node as Record<string, unknown>).anyOf as
        | unknown[]
        | undefined;
      const oneOf = (node as Record<string, unknown>).oneOf as
        | unknown[]
        | undefined;
      const first = (anyOf && anyOf[0]) || (oneOf && oneOf[0]);
      if (first) return inferLiteralTypeFromSchema(first);
      return 'json';
    },
    []
  );

  const fieldExpectedLiteralTypes = useMemo(() => {
    const map: Record<
      string,
      'string' | 'number' | 'boolean' | 'json' | 'null'
    > = {};
    if (
      isRecordFn(inputSchema) &&
      isRecordFn((inputSchema as Record<string, unknown>).properties)
    ) {
      const props = (inputSchema as Record<string, unknown>)
        .properties as Record<string, unknown>;
      for (const key of Object.keys(props)) {
        map[key] = inferLiteralTypeFromSchema(props[key]);
      }
    }
    return map;
  }, [inputSchema, inferLiteralTypeFromSchema]);

  const [mapping, setMapping] = useState<Record<string, MappingValue>>({});

  useEffect(() => {
    setMapping({});
  }, [selectedWorkflowId]);

  const isTimeTrigger = selectedTriggerId?.startsWith('comos.time.');
  const [scheduleRule, setScheduleRule] = useState<string>('');
  const [scheduleTz, setScheduleTz] = useState<string>('UTC');
  const selectedTrigger = useMemo(
    () => triggers.find((t) => String(t.triggerId) === selectedTriggerId),
    [triggers, selectedTriggerId]
  );

  const eventPathOptions = useMemo(() => {
    const schema = (
      selectedTrigger as unknown as { triggerDataSchema?: unknown }
    )?.triggerDataSchema;
    const paths: string[] = [];
    const visit = (node: unknown, prefix: string) => {
      if (!isRecordFn(node)) {
        if (prefix) paths.push(prefix);
        return;
      }
      const type = (node as Record<string, unknown>).type;
      if (
        type === 'object' ||
        (isRecordFn((node as Record<string, unknown>).properties) && !type)
      ) {
        const props = (node as Record<string, unknown>).properties as
          | Record<string, unknown>
          | undefined;
        if (props) {
          for (const [k, child] of Object.entries(props)) {
            visit(child, prefix ? `${prefix}.${k}` : k);
          }
          return;
        }
      }
      if (type === 'array' && 'items' in (node as Record<string, unknown>)) {
        visit(
          (node as Record<string, unknown>).items,
          prefix ? `${prefix}.0` : '0'
        );
        return;
      }
      const anyOf = (node as Record<string, unknown>).anyOf as
        | unknown[]
        | undefined;
      const oneOf = (node as Record<string, unknown>).oneOf as
        | unknown[]
        | undefined;
      if (Array.isArray(anyOf)) anyOf.forEach((n) => visit(n, prefix));
      else if (Array.isArray(oneOf)) oneOf.forEach((n) => visit(n, prefix));
      else if (prefix) paths.push(prefix);
    };
    if (schema) visit(schema, '');
    return Array.from(new Set(paths)).sort();
  }, [selectedTrigger]);

  const allValid = useMemo(() => {
    for (const key of requiredFields) {
      const m = mapping[key];
      if (!m || m.kind === 'empty') return false;
      if (m.kind === 'event' && !m.path.trim()) return false;
      if (m.kind === 'literal') {
        if (m.literalType === 'json') {
          try {
            JSON.parse(m.value);
          } catch {
            return false;
          }
        } else if (m.literalType === 'number') {
          if (Number.isNaN(Number(m.value))) return false;
        } else if (m.literalType === 'boolean') {
          if (m.value !== 'true' && m.value !== 'false') return false;
        } else {
          if (!m.value.trim()) return false;
        }
      }
    }
    return !!selectedTriggerId && !!selectedWorkflowId && !versionLoading;
  }, [
    mapping,
    requiredFields,
    selectedTriggerId,
    selectedWorkflowId,
    versionLoading,
  ]);

  const buildInstructions = () => {
    const instructions: Array<{
      workflowInputField: string;
      triggerDataPath: string;
    }> = [];
    for (const key of fieldKeys) {
      const m = mapping[key];
      if (!m || m.kind === 'empty') continue;
      if (m.kind === 'event') {
        if (m.path.trim()) {
          instructions.push({
            workflowInputField: key,
            triggerDataPath: m.path.trim(),
          });
        }
      } else if (m.kind === 'literal') {
        const value = m.value;
        let encoded = '';
        switch (m.literalType) {
          case 'string':
            encoded = `$literal:string:${value}`;
            break;
          case 'number':
            encoded = `$literal:number:${value}`;
            break;
          case 'boolean':
            encoded = `$literal:boolean:${value}`;
            break;
          case 'json':
            encoded = `$literal:json:${value}`;
            break;
          case 'null':
            encoded = `$literal:null:`;
            break;
        }
        instructions.push({
          workflowInputField: key,
          triggerDataPath: encoded,
        });
      }
    }
    return instructions;
  };

  const onConfirm = async () => {
    const instructions = buildInstructions();
    try {
      await createMutation.mutateAsync({
        workflowId: selectedWorkflowId,
        body: {
          version: selectedVersion,
          triggerId: selectedTriggerId,
          inputMapping: {
            mappingVersion: 0,
            instructions,
          },
        },
      });
      showAlert('Trigger creado correctamente', 'success'); // TODO: check when implementing i18n
      onCreated?.();
      onClose();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Error creando el trigger'; // TODO: check when implementing i18n
      showAlert(message, 'error'); // TODO: check when implementing i18n
    }
  };

  const header = (
    <Text size="h6" weight="semibold" className="text-gray-900">
      Crear automatización {/* TODO: check when implementing i18n */}
    </Text>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={header}
      body={
        <div className="flex flex-col w-full h-[min(80vh,720px)]">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 p-1 md:p-0 flex-none">
            <div className="md:col-span-4 space-y-3 border rounded-lg p-3 bg-white">
              <div className="flex items-center justify-between">
                <Text as="label" size="tiny" className="mb-1 block">
                  Seleccionar Gatillante{' '}
                  {/* TODO: check when implementing i18n */}
                </Text>
                {triggersLoading && (
                  <Loading size="2xs" appearance="outline" inline />
                )}
              </div>
              <Select
                value={selectedTriggerId}
                onChange={(e) =>
                  setSelectedTriggerId((e.target as HTMLSelectElement).value)
                }
                aria-label="Selector de gatillante"
                disabled={triggersLoading}
                options={triggerOptions}
              />
              {isTimeTrigger && (
                <div className="rounded border p-3 space-y-2">
                  <Text size="tiny" className="text-gray-700">
                    Configurar programación{' '}
                    {/* TODO: check when implementing i18n */}
                  </Text>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
                    <div className="md:col-span-2">
                      <Text as="label" size="tiny" className="mb-1 block">
                        Regla (RRULE) {/* TODO: check when implementing i18n */}
                      </Text>
                      <input
                        aria-label="RRULE"
                        className="w-full border rounded px-2 py-1 text-sm"
                        placeholder="FREQ=DAILY;BYHOUR=9"
                        value={scheduleRule}
                        onChange={(e) => setScheduleRule(e.target.value)}
                      />
                    </div>
                    <div>
                      <Text as="label" size="tiny" className="mb-1 block">
                        Zona horaria {/* TODO: check when implementing i18n */}
                      </Text>
                      <input
                        aria-label="Time zone"
                        className="w-full border rounded px-2 py-1 text-sm"
                        placeholder="UTC"
                        value={scheduleTz}
                        onChange={(e) => setScheduleTz(e.target.value)}
                      />
                    </div>
                  </div>
                  <Text size="tiny" className="text-gray-500">
                    La programación aún no se envía al backend desde este flujo.{' '}
                    {/* TODO: check when implementing i18n */}
                  </Text>
                </div>
              )}
            </div>

            <div className="md:col-span-8 space-y-3 border rounded-lg p-3 bg-white">
              <div className="flex items-center justify-between">
                <Text as="label" size="tiny" className="mb-1 block">
                  Seleccionar Flujo de trabajo{' '}
                  {/* TODO: check when implementing i18n */}
                </Text>
                {workflowsLoading && (
                  <Loading size="2xs" appearance="outline" inline />
                )}
              </div>
              {workflowsError && (
                <Text size="tiny" className="text-red-600 mb-1 block">
                  Error cargando workflows
                </Text>
              )}
              <Select
                value={selectedWorkflowId}
                onChange={(e) =>
                  setSelectedWorkflowId((e.target as HTMLSelectElement).value)
                }
                aria-label="Selector de flujo de trabajo"
                disabled={workflowsLoading}
                options={workflowOptions}
              />

              <div className="space-y-2 mt-2">
                <div className="flex items-center justify-between">
                  <Text as="label" size="tiny" className="mb-1 block">
                    Mapeo de entradas {/* TODO: check when implementing i18n */}
                  </Text>
                  {versionLoading && (
                    <Loading size="2xs" appearance="outline" inline />
                  )}
                </div>
                <div className="max-h-[60vh] overflow-auto pr-1">
                  {!versionLoading && (
                    <InputMappingSection
                      fieldKeys={fieldKeys}
                      requiredFields={requiredFields}
                      mapping={mapping}
                      eventPathOptions={eventPathOptions}
                      fieldExpectedLiteralTypes={fieldExpectedLiteralTypes}
                      onChange={(key, v) =>
                        setMapping((prev) => ({ ...prev, [key]: v }))
                      }
                      emptyMessage="Este flujo de trabajo no define entradas." // TODO: check when implementing i18n
                    />
                  )}
                  {versionLoading && (
                    <Text size="tiny" className="text-gray-600">
                      Cargando definición del workflow...{' '}
                      {/* TODO: check when implementing i18n */}
                    </Text>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      }
      footer={
        <ModalFooter
          onCancel={onClose}
          onConfirm={onConfirm}
          cancelText="Cancelar" // TODO: check when implementing i18n
          confirmText="Crear" // TODO: check when implementing i18n
          confirmDisabled={!allValid || createMutation.isPending}
          loading={createMutation.isPending}
          variant="primary"
        />
      }
      dialogClassName="sm:max-w-7xl lg:max-w-[1280px]"
    />
  );
};

export default CreateWorkflowTriggerModal;
